import React, { useEffect, useState, useRef } from 'react';
import { formatTime } from '../../../../utils';

export const ConversationContextModal = ({
    isOpen,
    onClose,
    messageId,
    fromNumber,
    initialLimitAnteriores = 10,
    initialLimitPosteriores = 10
}) => {
    const [messages, setMessages] = useState([]);
    const [isInitialLoading, setIsInitialLoading] = useState(false);
    const [isLoadingAnteriores, setIsLoadingAnteriores] = useState(false);
    const [isLoadingPosteriores, setIsLoadingPosteriores] = useState(false);
    const [hasMoreAnteriores, setHasMoreAnteriores] = useState(false);
    const [hasMorePosteriores, setHasMorePosteriores] = useState(false);
    const [limitAnteriores, setLimitAnteriores] = useState(initialLimitAnteriores);
    const [limitPosteriores, setLimitPosteriores] = useState(initialLimitPosteriores);

    const referencedRef = useRef(null);
    const chatContainerRef = useRef(null);
    const hasScrolledInitialRef = useRef(false);

    useEffect(() => {
        if (!isOpen) {
            setMessages([]);
            setLimitAnteriores(initialLimitAnteriores);
            setLimitPosteriores(initialLimitPosteriores);
            setHasMoreAnteriores(false);
            setHasMorePosteriores(false);
            setIsLoadingAnteriores(false);
            setIsLoadingPosteriores(false);
            hasScrolledInitialRef.current = false;
        }
    }, [isOpen, initialLimitAnteriores, initialLimitPosteriores]);

    useEffect(() => {
        if (!isOpen || !messageId) {
            setMessages([]);
            return;
        }

        const fetchInitialContext = async () => {
            setIsInitialLoading(true);
            try {
                const token = localStorage.getItem('token') || sessionStorage.getItem('token');

                const queryParams = new URLSearchParams({
                    previousLimit: String(initialLimitAnteriores),
                    subsequentLimit: String(initialLimitPosteriores)
                });
                if (fromNumber) {
                    queryParams.append('fromNumber', fromNumber);
                }

                const response = await fetch(`/api/SupportEngine/GetReferencedMessageContext/${messageId}?${queryParams.toString()}`, {
                    headers: {
                        Accept: 'application/json',
                        ...(token ? { Authorization: 'Bearer ' + token } : {})
                    }
                });
                const json = await response.json();
                const data = (json && Array.isArray(json.data)) ? json.data : (Array.isArray(json) ? json : []);

                setMessages(data);

                const countAnteriores = data.filter(m => m.idMensaje < messageId).length;
                const countPosteriores = data.filter(m => m.idMensaje > messageId).length;
                setHasMoreAnteriores(countAnteriores >= initialLimitAnteriores);
                setHasMorePosteriores(countPosteriores >= initialLimitPosteriores);
            } catch (error) {
                console.error('Error al obtener contexto de la conversación:', error);
                setMessages([]);
                setHasMoreAnteriores(false);
                setHasMorePosteriores(false);
            } finally {
                setIsInitialLoading(false);
            }
        };

        fetchInitialContext();
    }, [isOpen, messageId, fromNumber, initialLimitAnteriores, initialLimitPosteriores]);

    // Auto-scroll al mensaje referenciado una sola vez al cargar la conversación inicialmente
    useEffect(() => {
        if (!isInitialLoading && messages.length > 0 && referencedRef.current && !hasScrolledInitialRef.current) {
            referencedRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            hasScrolledInitialRef.current = true;
        }
    }, [isInitialLoading, messages]);

    const handleLoadMoreAnteriores = async () => {
        if (isLoadingAnteriores || !hasMoreAnteriores) return;

        setIsLoadingAnteriores(true);
        const nextLimit = limitAnteriores + 10;

        try {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const queryParams = new URLSearchParams({
                previousLimit: String(nextLimit),
                subsequentLimit: String(limitPosteriores)
            });
            if (fromNumber) {
                queryParams.append('fromNumber', fromNumber);
            }

            const response = await fetch(`/api/SupportEngine/GetReferencedMessageContext/${messageId}?${queryParams.toString()}`, {
                headers: {
                    Accept: 'application/json',
                    ...(token ? { Authorization: 'Bearer ' + token } : {})
                }
            });
            const json = await response.json();
            const incoming = (json && Array.isArray(json.data)) ? json.data : (Array.isArray(json) ? json : []);

            const incomingAnteriores = incoming.filter(m => m.idMensaje < messageId);
            const currentAnterioresCount = messages.filter(m => m.idMensaje < messageId).length;

            if (incomingAnteriores.length <= currentAnterioresCount || incomingAnteriores.length < nextLimit) {
                setHasMoreAnteriores(false);
            }

            const container = chatContainerRef.current;
            const prevScrollHeight = container ? container.scrollHeight : 0;
            const prevScrollTop = container ? container.scrollTop : 0;

            setMessages(prev => {
                const map = new Map();
                [...prev, ...incoming].forEach(msg => {
                    if (msg && msg.idMensaje) {
                        map.set(msg.idMensaje, msg);
                    }
                });
                return Array.from(map.values()).sort((a, b) => a.idMensaje - b.idMensaje);
            });

            setLimitAnteriores(nextLimit);

            requestAnimationFrame(() => {
                if (container) {
                    const diff = container.scrollHeight - prevScrollHeight;
                    container.scrollTop = prevScrollTop + diff;
                }
            });
        } catch (error) {
            console.error('Error al cargar mensajes anteriores:', error);
        } finally {
            setIsLoadingAnteriores(false);
        }
    };

    const handleLoadMorePosteriores = async () => {
        if (isLoadingPosteriores || !hasMorePosteriores) return;

        setIsLoadingPosteriores(true);
        const nextLimit = limitPosteriores + 10;

        try {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const queryParams = new URLSearchParams({
                previousLimit: String(limitAnteriores),
                subsequentLimit: String(nextLimit)
            });
            if (fromNumber) {
                queryParams.append('fromNumber', fromNumber);
            }

            const response = await fetch(`/api/SupportEngine/GetReferencedMessageContext/${messageId}?${queryParams.toString()}`, {
                headers: {
                    Accept: 'application/json',
                    ...(token ? { Authorization: 'Bearer ' + token } : {})
                }
            });
            const json = await response.json();
            const incoming = (json && Array.isArray(json.data)) ? json.data : (Array.isArray(json) ? json : []);

            const incomingPosteriores = incoming.filter(m => m.idMensaje > messageId);
            const currentPosterioresCount = messages.filter(m => m.idMensaje > messageId).length;

            if (incomingPosteriores.length <= currentPosterioresCount || incomingPosteriores.length < nextLimit) {
                setHasMorePosteriores(false);
            }

            setMessages(prev => {
                const map = new Map();
                [...prev, ...incoming].forEach(msg => {
                    if (msg && msg.idMensaje) {
                        map.set(msg.idMensaje, msg);
                    }
                });
                return Array.from(map.values()).sort((a, b) => a.idMensaje - b.idMensaje);
            });

            setLimitPosteriores(nextLimit);
        } catch (error) {
            console.error('Error al cargar mensajes posteriores:', error);
        } finally {
            setIsLoadingPosteriores(false);
        }
    };

    if (!isOpen) return null;


    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 9999,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                backdropFilter: 'blur(3px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '16px'
            }}
            onClick={onClose}
        >
            {/* Contenedor del Modal */}
            <div
                style={{
                    backgroundColor: '#efeae2',
                    width: '100%',
                    maxWidth: '680px',
                    maxHeight: '85vh',
                    borderRadius: '16px',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '14px 20px',
                        backgroundColor: '#f0f2f5',
                        borderBottom: '1px solid #e2e8f0'
                    }}
                >
                    <div>
                        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
                            Conversacion del mensaje
                        </h3>
                        <span style={{ fontSize: '12px', color: '#64748b' }}>
                            Mensaje #{messageId}
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '18px',
                            color: '#64748b',
                            padding: '6px 10px',
                            borderRadius: '50%'
                        }}
                    >
                        ✕
                    </button>
                </div>

                {/* Cuerpo del Chat */}
                <div
                    ref={chatContainerRef}
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        backgroundImage: 'radial-gradient(#cbd5e1 0.75px, transparent 0.75px)',
                        backgroundSize: '16px 16px'
                    }}
                >
                    {isInitialLoading ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b', fontSize: '14px' }}>
                            Cargando mensajes de la conversación...
                        </div>
                    ) : messages.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b', fontSize: '14px' }}>
                            No se encontraron mensajes en esta conversación.
                        </div>
                    ) : (
                        <>
                            {/* Botón para cargar más mensajes anteriores */}
                            {hasMoreAnteriores && (
                                <div style={{ textAlign: 'center', margin: '2px 0 8px' }}>
                                    <button
                                        type="button"
                                        disabled={isLoadingAnteriores}
                                        onClick={handleLoadMoreAnteriores}
                                        style={{
                                            fontSize: '11px',
                                            padding: '4px 12px',
                                            borderRadius: '16px',
                                            border: '1px solid #cbd5e1',
                                            backgroundColor: '#ffffff',
                                            color: '#475569',
                                            cursor: isLoadingAnteriores ? 'not-allowed' : 'pointer',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                        }}
                                        title="Cargar mensajes anteriores"
                                    >
                                        {isLoadingAnteriores ? 'Cargando anteriores...' : 'Cargar +10 anteriores'}
                                    </button>
                                </div>
                            )}

                            {messages.map((msg) => {
                                const isTarget = msg.idMensaje === messageId;
                                const isAgent = Boolean(msg.bEsAgente);
                                const targetMsg = messages.find(m => m.idMensaje === messageId);
                                const isOtherTicket = Boolean(targetMsg && msg.idTicket && targetMsg.idTicket && msg.idTicket !== targetMsg.idTicket);

                                // Obtiene la URL del recurso multimedia (URL directa o por GetMedia con vMediaId)
                                const fileUrl = msg.vMediaUrl
                                    ? msg.vMediaUrl
                                    : (msg.vMediaId ? `/api/SupportEngine/GetMedia/${msg.vMediaId}` : null);

                                return (
                                    <div
                                        key={msg.idMensaje}
                                        ref={isTarget ? referencedRef : null}
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: isAgent ? 'flex-end' : 'flex-start',
                                            width: '100%'
                                        }}
                                    >
                                        <div
                                            style={{
                                                maxWidth: '78%',
                                                borderRadius: '12px',
                                                padding: '8px 12px',
                                                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                                backgroundColor: isAgent ? '#d9fdd3' : '#ffffff',
                                                color: '#111827',
                                                fontSize: '13.5px',
                                                border: isTarget ? '2px solid #10b981' : 'none',
                                                position: 'relative'
                                            }}
                                        >
                                            {/* Badge identificador de Ticket */}
                                            {msg.idTicket && (
                                                <div
                                                    style={{
                                                        fontSize: '10px',
                                                        fontWeight: 600,
                                                        color: isOtherTicket ? '#b45309' : '#475569',
                                                        backgroundColor: isOtherTicket ? '#fef3c7' : '#f1f5f9',
                                                        border: isOtherTicket ? '1px solid #fde68a' : '1px solid #e2e8f0',
                                                        padding: '1px 6px',
                                                        borderRadius: '4px',
                                                        marginBottom: '4px',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '4px',
                                                        width: 'fit-content'
                                                    }}
                                                >
                                                    {isOtherTicket && <span>⚠️</span>}
                                                    <span>Ticket #{msg.idTicket}</span>
                                                    {isOtherTicket && <span style={{ fontSize: '9px', opacity: 0.85 }}>(ticket distinto)</span>}
                                                </div>
                                            )}

                                            {/* Badge indicador del mensaje referenciado */}
                                            {isTarget && (
                                                <div
                                                    style={{
                                                        fontSize: '11px',
                                                        fontWeight: 600,
                                                        color: '#059669',
                                                        marginBottom: '4px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}
                                                >
                                                    📌 Mensaje citado
                                                </div>
                                            )}

                                        {/* 📷 Imagen (Tipo 2) */}
                                        {fileUrl && msg.idCatTipoMensaje === 2 && (
                                            <img
                                                src={fileUrl}
                                                alt="Imagen"
                                                style={{
                                                    borderRadius: '8px',
                                                    maxHeight: '220px',
                                                    width: '100%',
                                                    objectFit: 'cover',
                                                    marginBottom: '6px',
                                                    display: 'block'
                                                }}
                                            />
                                        )}

                                        {/* 💟 Sticker (Tipo 3) */}
                                        {fileUrl && msg.idCatTipoMensaje === 3 && (
                                            <img
                                                src={fileUrl}
                                                alt="Sticker"
                                                style={{
                                                    width: '120px',
                                                    height: '120px',
                                                    objectFit: 'contain',
                                                    marginBottom: '6px',
                                                    display: 'block'
                                                }}
                                            />
                                        )}

                                        {/* 🎤 Nota de voz / Audio (Tipo 4) */}
                                        {fileUrl && msg.idCatTipoMensaje === 4 && (
                                            <div style={{ marginBottom: '6px' }}>
                                                <audio controls src={fileUrl} style={{ width: '100%', height: '36px' }} />
                                            </div>
                                        )}

                                        {/* 🎥 Video (Tipo 5) */}
                                        {fileUrl && msg.idCatTipoMensaje === 5 && (
                                            <video
                                                controls
                                                src={fileUrl}
                                                style={{
                                                    borderRadius: '8px',
                                                    maxHeight: '220px',
                                                    width: '100%',
                                                    marginBottom: '6px',
                                                    display: 'block'
                                                }}
                                            />
                                        )}

                                        {/* 📄 Documento (Tipo 6) */}
                                        {fileUrl && msg.idCatTipoMensaje === 6 && (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    gap: '10px',
                                                    padding: '6px 10px',
                                                    backgroundColor: 'rgba(0,0,0,0.05)',
                                                    borderRadius: '8px',
                                                    marginBottom: '6px'
                                                }}
                                            >
                                                <span style={{ fontSize: '12px' }}>📄 Archivo adjunto</span>
                                                <a
                                                    href={fileUrl}
                                                    download
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        fontSize: '11.5px',
                                                        color: '#0284c7',
                                                        fontWeight: 600,
                                                        textDecoration: 'none'
                                                    }}
                                                >
                                                    Descargar
                                                </a>
                                            </div>
                                        )}

                                        {/* Texto del mensaje */}
                                        {msg.vTextoMensaje && (
                                            <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>
                                                {msg.vTextoMensaje}
                                            </div>
                                        )}

                                        {/* Hora */}
                                        <div
                                            style={{
                                                fontSize: '10px',
                                                color: '#6b7280',
                                                textAlign: 'right',
                                                marginTop: '4px'
                                            }}
                                        >
                                            {formatTime(msg.dCreatedDate)}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        {/* Botón para cargar más mensajes posteriores */}
                        {hasMorePosteriores && (
                            <div style={{ textAlign: 'center', margin: '8px 0 2px' }}>
                                <button
                                    type="button"
                                    disabled={isLoadingPosteriores}
                                    onClick={handleLoadMorePosteriores}
                                    style={{
                                        fontSize: '11px',
                                        padding: '4px 12px',
                                        borderRadius: '16px',
                                        border: '1px solid #cbd5e1',
                                        backgroundColor: '#ffffff',
                                        color: '#475569',
                                        cursor: isLoadingPosteriores ? 'not-allowed' : 'pointer',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                    }}
                                    title="Cargar mensajes posteriores"
                                >
                                    {isLoadingPosteriores ? 'Cargando posteriores...' : 'Cargar +10 posteriores'}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

                {/* Footer */}
                <div
                    style={{
                        padding: '10px 16px',
                        backgroundColor: '#f0f2f5',
                        borderTop: '1px solid #e2e8f0',
                        textAlign: 'center',
                        fontSize: '12px',
                        color: '#64748b'
                    }}
                >
                    Solo lectura.
                </div>
            </div>
        </div>
    );
};
